/**
 * useWebinarStreamsHandler
 *
 * Architecture (Webinar):
 * - Primary (camera/mic) client: created by the Provider. Its UID/token are **provided by BE**
 *   via `agoraStreamingData` and passed to `useJoin` in the context, which joins the channel.
 * - Secondary (screen share) client: created on-demand here. Its UID/token are **provided by BE**
 *   via `shareScreenStreamCredentials` received from the SHARE_SCREEN socket flow.
 *
 * Local tiles:
 * - Camera tile → id = String(cameraClientId)       (camera/mic client's uid)
 * - Screen tile → id = String(screenUid)            (screen client uid from BE credentials)
 *   We always append a separate screen tile; we never update the camera tile.
 *
 * Remote tiles:
 * - Peer camera → id = String(peer.stream_uid)
 * - Peer screen → id = String(peer.peer_info.peer_screen_stream_id)
 *
 * Rules:
 * - Share ON → append screen tile (separate), do not touch camera tile.
 * - Share OFF → remove only the screen tile.
 * - Camera toggle → update (or clear) the camera tile only.
 * - Remote users → screen and camera are independent tiles; never remove a camera tile
 *   just because a screen tile appeared.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import AgoraRTC, {
  ICameraVideoTrack,
  IAgoraRTCRemoteUser,
  IMicrophoneAudioTrack,
  useRTCClient,
  ILocalVideoTrack,
  ILocalAudioTrack,
} from 'agora-rtc-react';

import { throttle } from 'lodash';

import SocketService from '@src/services/SocketService';
import {
  useRoomStore,
  usePeersStore,
  useVideoStreamStore,
  useRoomActionsStore,
  useMediaSettingsStore,
  useWebinarRoomStore,
} from '@src/stores';
import { usePeerActions } from '@src/hooks';
import { EWebinarSockets } from '@src/constants';
import useRoomContext from '@src/contexts/useRoomContext';
import { IPeer } from '@src/types';

interface IProps {
  isActive: boolean;
  localCameraTrack: ICameraVideoTrack | null;
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  // screen credentials (uid + token) provided by BE through SHARE_SCREEN socket
  shareScreenStreamCredentials?: { stream_uid?: number; token?: string } | null;
}

const useWebinarStreamsHandler = ({
  isActive,
  localCameraTrack,
  localMicrophoneTrack,
  shareScreenStreamCredentials,
}: IProps) => {
  // main client (camera/mic) comes from context provider
  const client = useRTCClient();

  // dedicated RTC client for screen share (joins when sharing starts using BE-provided creds)
  const screenClientRef = useRef<ReturnType<typeof AgoraRTC.createClient> | null>(null);
  const screenVideoRef = useRef<ILocalVideoTrack | null>(null);
  const screenAudioRef = useRef<ILocalAudioTrack | null>(null);
  const creatingScreenRef = useRef(false);

  const latestVolumes = useRef<{ level: number; uid: number | string }[]>([]);

  const { currentPeer } = useRoomContext();
  const { handleUpdatePeerInfo } = usePeerActions(currentPeer);

  // Throttle emit function to run at most once every 2 seconds
  const emitVolumeThrottled = throttle(() => {
    const speakers = latestVolumes.current;
    const mutedVolumes = speakers.every(({ level }) => level === 0);
    !mutedVolumes &&
      SocketService.emit(EWebinarSockets.VOLUME_INDICATOR, {
        roomId: webinarId,
        speakers,
      });
  }, 2000);

  const { isMicClosed, isCamClosed, isScreenSharing, setShareScreenSettings } = useRoomActionsStore(
    useShallow((state) => ({
      isMicClosed: state.micSettings.isMicClosed,
      isCamClosed: state.camSettings.isCamClosed,
      isScreenSharing: state.shareScreenSettings.isActive,
      setShareScreenSettings: state.setShareScreenSettings,
    }))
  );

  const { selectedVideoInputDevice } = useMediaSettingsStore(
    useShallow((state) => ({
      selectedVideoInputDevice: state.mediaSettings.selectedVideoInputDevice,
    }))
  );

  const { webinarId } = useWebinarRoomStore(
    useShallow((state) => ({
      webinarId: state.webinarId,
    }))
  );

  const { modifiedStream } = useVideoStreamStore(
    useShallow((state) => ({
      modifiedStream: state.modifiedStream,
    }))
  );

  const appId = process.env.REACT_APP_AGORA_API_ID ?? '';
  const cameraUid = client?.uid;
  const cameraClientId = cameraUid?.toString();

  const screenUid = shareScreenStreamCredentials?.stream_uid;
  const screenToken = shareScreenStreamCredentials?.token;

  /** ---------- LOCAL: camera tile handler (independent from screen) ---------- */
  const handleLocalVideo = useCallback(async () => {
    if (!cameraClientId) return;

    // ensure the selected camera device is applied
    await localCameraTrack?.setDevice(selectedVideoInputDevice?.deviceId || 'default');

    const track = localCameraTrack?.enabled ? localCameraTrack.getMediaStreamTrack() : null;
    const stream = track ? new MediaStream([track]) : null;
    useVideoStreamStore.getState().setOriginalStream(stream);
    const isExisting = useVideoStreamStore.getState().videoStreams.findIndex((v) => v.id === cameraClientId) > -1;
    // update the local camera tile (we never replace it with screen)
    !isExisting &&
      useVideoStreamStore.getState().appendVideo({
        id: cameraClientId,
        stream,
        peerId: SocketService.getSocketId() ?? '',
        peerName: useRoomStore.getState().peerName,
        type: 'videoType',
        volume: 0,
        audioVolume: 0,
        autoPlay: true,
        isPresenter: false,
        muted: true,
        poster: '',
        isCurrentUser: true,
      });
  }, [cameraClientId, localCameraTrack, selectedVideoInputDevice?.deviceId]);

  /** ---- REMOTE: append camera/screen as separate tiles ---- */
  const handleAppendRemoteVideo = useCallback(async (user: IAgoraRTCRemoteUser, stream: MediaStream | null = null) => {
    let remotePeer: IPeer | undefined;
    let retries = 0;
    // map Agora uid to our peer record (either camera uid or screen uid)
    while (!remotePeer && retries < 10) {
      const peers = Array.from(usePeersStore.getState().peers.values());
      remotePeer = peers.find(
        ({ stream_uid, peer_info: { peer_screen, peer_screen_stream_id } }) =>
          stream_uid === user.uid || (peer_screen && peer_screen_stream_id === user.uid)
      );
      if (!remotePeer) {
        console.log(`[WEBINAR] Remote peer not found for user ${user.uid}, retry ${retries + 1}/10`);
        await new Promise((res) => setTimeout(res, 200)); // Increase delay
        retries++;
      }
    }

    if (!remotePeer) {
      console.warn('[WEBINAR] Remote peer not found after waiting:', user.uid);
      useWebinarRoomStore.getState().removeViewerPinnedPeerUids([+user.uid]);
      return;
    }
    // check if this uid corresponds to the peer's screen stream uid

    const isScreenUid =
      !!remotePeer?.peer_info?.peer_screen && remotePeer?.peer_info?.peer_screen_stream_id === user.uid;

    // append a tile for **this uid** (do not remove their camera tile on screen publish)

    useVideoStreamStore.getState().appendVideo({
      id: user.uid.toString(),
      stream,
      peerId: remotePeer.peer_info.peer_id || remotePeer.id || '',
      peerName: remotePeer.peer_info.peer_name ?? '',
      volume: 0,
      audioVolume: 0,
      autoPlay: true,
      isPresenter: false,
      muted: true,
      type: isScreenUid ? 'screenType' : 'videoType',
      isPinned: isScreenUid,
      poster: '',
    });
  }, []);

  /** ---------- Local device enable/disable wiring ---------- */
  useEffect(() => {
    localMicrophoneTrack?.setEnabled(!isMicClosed);
  }, [isMicClosed, localMicrophoneTrack]);

  useEffect(() => {
    // keep camera enabled state and device in sync with settings
    const handleLocalCameraTrack = async () => {
      try {
        await localCameraTrack?.setEnabled(!isCamClosed);

        // Ensure the video element is updated with the new track
        const track = localCameraTrack?.enabled ? localCameraTrack.getMediaStreamTrack() : null;
        const stream = track ? new MediaStream([track]) : null;

        // Update the video store with the new stream
        cameraClientId &&
          useVideoStreamStore.getState().updateVideo(cameraClientId, { stream }, undefined, 'videoType');
      } catch (err) {
        console.error('[WEBINAR] Error enabling local camera track:', err);
      }
      handleLocalVideo();
    };

    handleLocalCameraTrack();
  }, [cameraClientId, handleLocalVideo, isCamClosed, localCameraTrack]);

  useEffect(() => {
    const originalStream = useVideoStreamStore.getState().originalStream;
    const latestTrack = modifiedStream ? modifiedStream?.getVideoTracks()?.[0] : originalStream?.getVideoTracks()?.[0];

    latestTrack && localCameraTrack?.replaceTrack(latestTrack, false);
  }, [localCameraTrack, modifiedStream]);

  /** ---------- LOCAL: screen tile helpers (always separate tile, BE-provided uid) ---------- */

  const appendLocalScreenTile = useCallback(
    (screenVideo: ILocalVideoTrack) => {
      if (!screenUid) return;
      useVideoStreamStore.getState().appendVideo({
        id: String(screenUid),
        stream: new MediaStream([screenVideo.getMediaStreamTrack()]),
        peerId: SocketService.getSocketId() ?? '',
        peerName: useRoomStore.getState().peerName,
        type: 'screenType',
        isPinned: true,
        isCurrentUser: true,
        autoPlay: true,
        muted: true,
        volume: 0,
        audioVolume: 0,
        poster: '',
        isPresenter: false,
      });
    },
    [screenUid]
  );

  const removeLocalScreenTile = useCallback(() => {
    if (!screenUid) return;
    useVideoStreamStore.getState().removeVideo(String(screenUid));
  }, [screenUid]);

  /** ---------- LOCAL: stop screen share (abandon second client) ---------- */
  const stopScreenInternal = useCallback(async () => {
    // broadcast "screen off" so peers can clear their screen tiles for us
    const peer_id = currentPeer?.peer_info?.peer_id;
    if (peer_id) {
      try {
        handleUpdatePeerInfo({ peer_id, type: 'screenType', status: false, emit: true });
      } catch (err) {
        console.warn('[WEBINAR] removeScreen (announce off) failed', err);
      }
    }

    // unpublish and clean up the screen client + tracks
    try {
      if (screenClientRef.current) {
        const cli = screenClientRef.current;
        if (screenVideoRef.current) await cli.unpublish(screenVideoRef.current);
        if (screenAudioRef.current) await cli.unpublish(screenAudioRef.current as any);
      }
    } catch (err) {
      console.error('[WEBINAR] removeScreen: unpublish failed', err);
    }

    screenVideoRef.current?.stop();
    screenVideoRef.current?.close();
    screenAudioRef.current?.stop?.();
    screenAudioRef.current?.close?.();

    screenVideoRef.current = null;
    screenAudioRef.current = null;

    try {
      if (screenClientRef.current) {
        await screenClientRef.current.leave();
        screenClientRef.current = null;
      }
    } catch (err) {
      console.warn('[WEBINAR] removeScreen: leave failed', err);
    }

    // remove only the screen tile (camera tile stays as is)
    removeLocalScreenTile();
  }, [currentPeer?.peer_info?.peer_id, handleUpdatePeerInfo, removeLocalScreenTile]);

  /** ---------- MAIN EFFECT: start/stop screen share (second client lifecycle) ---------- */
  useEffect(() => {
    let cancelled = false;

    const startScreen = async () => {
      if (!isActive || creatingScreenRef.current || screenVideoRef.current) return;

      // must have BE-provided creds for screen client
      if (!webinarId || !screenUid || !screenToken || !appId) {
        console.warn('[WEBINAR] Screen share missing credentials/appid/channel');
        setShareScreenSettings({ isActive: false });
        return;
      }

      creatingScreenRef.current = true;
      try {
        // create & join the second screen client
        const screenClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        screenClientRef.current = screenClient;

        await screenClient.join(appId, webinarId, screenToken, screenUid);

        // create screen tracks (may return [video, audio] or just video)
        const created = await AgoraRTC.createScreenVideoTrack({}, 'auto');

        // if effect was cleaned up mid-creation, close the tracks to avoid leaks
        if (cancelled) {
          Array.isArray(created)
            ? created.forEach((track) => {
                try {
                  track?.stop?.();
                  track?.close?.();
                } catch {}
              })
            : created.close();
          return;
        }

        let screenVideo: ILocalVideoTrack | null = null;
        let screenAudio: ILocalAudioTrack | null = null;

        if (Array.isArray(created)) {
          [screenVideo, screenAudio] = created;
        } else {
          screenVideo = created;
        }

        if (!screenVideo) {
          setShareScreenSettings({ isActive: false });
          return;
        }

        screenVideoRef.current = screenVideo;
        screenAudioRef.current = screenAudio ?? null;

        // pre-announce to peers: tell them our screen uid so they can map tiles
        if (currentPeer?.peer_info?.peer_id) {
          try {
            handleUpdatePeerInfo({
              peer_id: currentPeer.peer_info.peer_id,
              type: 'screenType',
              status: true,
              extraData: { peer_screen_stream_id: shareScreenStreamCredentials.stream_uid },
              emit: true,
            });
          } catch (err) {
            console.warn('[WEBINAR] pre-announce screen on failed', err);
          }
        }

        // publish screen video (+ optional system audio)
        const pubs: any[] = [screenVideo];
        if (screenAudioRef.current) pubs.push(screenAudioRef.current);
        await screenClient.publish(pubs);

        // locally show the screen as an independent tile (id = screenUid)
        appendLocalScreenTile(screenVideo);

        // Clear loading state on success
        setShareScreenSettings({ isLoading: false });

        // if the browser stops capture, reflect it in UI state
        screenVideo.on('track-ended', () => setShareScreenSettings({ isActive: false }));
      } catch (err) {
        console.warn('[WEBINAR] ScreenShare start failed:', err);
        setShareScreenSettings({ isActive: false, isLoading: false });
      } finally {
        creatingScreenRef.current = false;
      }
    };

    const stopScreen = async () => {
      if (screenVideoRef.current || screenAudioRef.current || screenClientRef.current) {
        await stopScreenInternal();
      }
    };

    if (isScreenSharing && isActive) startScreen();
    else stopScreen();

    return () => {
      cancelled = true; // prevents using created tracks if effect is torn down
    };
  }, [
    isScreenSharing,
    isActive,
    appId,
    webinarId,
    screenUid,
    screenToken,
    setShareScreenSettings,
    stopScreenInternal,
    currentPeer,
    handleUpdatePeerInfo,
    shareScreenStreamCredentials?.stream_uid,
    appendLocalScreenTile,
  ]);

  useEffect(() => {
    if (isActive && client) {
      // Process users already in the channel
      const existingUsers = client.remoteUsers;
      existingUsers.forEach((user) => {
        handleAppendRemoteVideo(user, null);
      });
    }
  }, [isActive, client, handleAppendRemoteVideo]);

  // Register listeners as soon as client is available
  useEffect(() => {
    if (!client) return;

    // ignore our own screen client on the main client
    const handleUserJoined = (user: IAgoraRTCRemoteUser) => {
      if (user.uid === screenUid) return;
      handleAppendRemoteVideo(user, null);
    };

    client.on('user-joined', handleUserJoined);

    return () => {
      client.off('user-joined', handleUserJoined);
    };
  }, [client, handleAppendRemoteVideo, screenUid]);

  /** ---------- REMOTE: subscribe and keep both tiles (cam + screen) ---------- */
  useEffect(() => {
    if (!isActive || !cameraClientId || !client) return;

    const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      await client.subscribe(user, mediaType);
      if (mediaType !== 'video' || !user.videoTrack) return;

      // ignore my own screen client locally
      if (user.uid === screenUid) return;

      const peers = Array.from(usePeersStore.getState().peers.values());
      const remotePeer = peers.find(({ stream_uid, peer_info: { peer_screen_stream_id } }) =>
        [stream_uid, peer_screen_stream_id].includes(+user.uid)
      );

      const isScreenUid =
        !!remotePeer?.peer_info?.peer_screen && remotePeer?.peer_info?.peer_screen_stream_id === user.uid;

      // append the stream for this uid; DO NOT remove the camera tile
      const videoStream = new MediaStream([user.videoTrack.getMediaStreamTrack()]);
      useVideoStreamStore.getState().appendVideo({
        id: String(user.uid),
        stream: videoStream,
        peerId: remotePeer?.peer_info?.peer_id || remotePeer?.id || '',
        peerName: remotePeer?.peer_info?.peer_name ?? '',
        volume: 0,
        audioVolume: 0,
        autoPlay: true,
        isPresenter: false,
        muted: true,
        type: isScreenUid ? 'screenType' : 'videoType',
        isPinned: isScreenUid,
        poster: '',
      });
    };

    const handleUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      client.unsubscribe(user, mediaType);
      if (mediaType !== 'video') return;

      const peers = Array.from(usePeersStore.getState().peers.values());
      const remotePeer = peers.find(({ stream_uid, peer_info: { peer_screen_stream_id } }) =>
        [stream_uid, peer_screen_stream_id].includes(+user.uid)
      );

      // unknown uid → clear whatever we have for it
      if (!remotePeer) {
        useVideoStreamStore.getState().updateVideo(String(user.uid), { stream: null, isPinned: false });
        return;
      }

      const isScreenUid =
        !!remotePeer.peer_info?.peer_screen && remotePeer.peer_info.peer_screen_stream_id === user.uid;

      if (isScreenUid) {
        // screen unpublished → remove only screen tile
        useVideoStreamStore.getState().removeVideo(String(user.uid));
        return;
      }

      // camera unpublished → keep camera tile but clear stream
      useVideoStreamStore.getState().updateVideo(String(user.uid), { stream: null, isPinned: false });
    };

    const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
      useVideoStreamStore.getState().removeVideo(String(user.uid));

      useWebinarRoomStore.getState().removeViewerPinnedPeerUids([+user.uid]);
    };

    const handleRemoteAudioVolume = (volumes: { level: number; uid: number | string }[]) => {
      if (!volumes?.length) return;

      const { isReporter, updateVideo } = useVideoStreamStore.getState();

      // Always update the latest volumes
      latestVolumes.current = volumes;

      if (isReporter) {
        emitVolumeThrottled();
      }

      volumes.forEach((volume) => {
        updateVideo(volume.uid.toString(), {
          audioVolume: Math.round(Math.max(0, Math.min(5, volume.level / 20))),
          muted: volume.level <= 0,
        });
      });
    };

    client.enableAudioVolumeIndicator();
    client.on('user-left', handleUserLeft);
    client.on('user-published', handleUserPublished);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('volume-indicator', handleRemoteAudioVolume);

    return () => {
      client.off('user-left', handleUserLeft);
      client.off('user-published', handleUserPublished);
      client.off('user-unpublished', handleUserUnpublished);
      client.off('volume-indicator', handleRemoteAudioVolume);
    };
  }, [isActive, client, cameraClientId, handleAppendRemoteVideo, screenUid, webinarId, emitVolumeThrottled]);
};

export default useWebinarStreamsHandler;
